import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  InjectionToken,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { SharedComponent, ShareUserViewModel, UserSelectValue } from '@dntlab/share-files-dialog';
import { TranslateService } from '@ngx-translate/core';
import { DropdownSettings } from 'angular2-multiselect-dropdown/lib/multiselect.interface';
import { Observable } from 'rxjs';
import AppSettings from 'src/app/common/app.settings';
import AppValues from 'src/app/common/app.values';
import { SendMessageFormService } from 'src/app/forms/conversation-forms/send-message-form.service';
import { DialogSendMessageData, MessageLevel } from 'src/app/interfaces';
import { FormGroupControls } from 'src/app/interfaces/form-and-field.interface';
import { UsersSuggestionService } from 'src/app/services/users-suggestion.service';
import { safelyDetectChanges } from '../../../common/app.helpers';
import { NotificationState } from '../../notifications.state';
import { DialogActionTypes } from '../dialogs.enum';
import { DialogMessagePlaceholders } from './dialog-message-placeholders.model';
import { DialogStrategy, NotSelectedUsers, SearchField, SelectedUsers } from './dialog-message.strategy';
import { shareUserViewModelToUserSelectValueAdapter } from './dialogs.adapter';

export const DIALOG_SEND_MESSAGE_DATA = new InjectionToken<DialogSendMessageData>(`DIALOG_DEFAULT_DATA`);
export interface MultiSelectSettings extends DropdownSettings {
  text: string;
  singleSelection: boolean;
  enableSearchFilter: boolean;
  enableCheckAll: boolean;
  classes: string;
}

@Component({
  selector: 'app-dialog-message',
  templateUrl: './dialog-message.component.html',
  styleUrls: ['./dialog-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogMessageComponent extends NotificationState implements SharedComponent, OnInit, OnDestroy {
  @ViewChild('searchInput', { static: false }) public searchInput: ElementRef<HTMLInputElement>;

  private detector = inject(ChangeDetectorRef);
  private sendMessageFormService = inject(SendMessageFormService);
  private translateService = inject(TranslateService);
  private usersSuggestionService = inject(UsersSuggestionService);

  protected notificationLevel: MessageLevel = 'INFO';

  public readonly hasUnsavedChanges$: Observable<boolean> = this.sendMessageFormService.formChanged$;
  public readonly dialogData: DialogSendMessageData = inject(DIALOG_SEND_MESSAGE_DATA);

  public readonly DEBOUNCE_TIME: number = AppValues.DEBOUNCE_TIME;
  public readonly FALLBACK_IMAGE: string = AppSettings.NO_PHOTO_DEFAULT_IMAGE;

  public readonly placeholderPhrases: DialogMessagePlaceholders = {
    inputPlaceholder: 'CHAT_DIALOG_TEXTAREA_PLACEHOLDER',
    maxLengthErrorHint: 'CHAT_DIALOG_LARGE_MESSAGE_TEXT_ERROR',
    emptyErrorHint: 'CHAT_DIALOG_EMPTY_ERROR_HINT',
    buttonHint: 'CHAT_MESSAGE_FORM_SEND_BUTTON_HINT',
    typingHint: '',
  };

  public readonly searchSettings: MultiSelectSettings = {
    singleSelection: false,
    enableSearchFilter: true,
    enableCheckAll: false,
    classes: '',
    tagToBody: false,
    text: this.translateService.instant('CHAT_SEARCH_INPUT_PLACEHOLDER') as string,
  };

  public readonly dialogStrategy: DialogStrategy = this.dialogData.users.length
    ? new SelectedUsers(this.activeModal, this.dialogData.users, this.sendMessageFormService)
    : new NotSelectedUsers(this.activeModal, this.sendMessageFormService);

  public readonly messageForm: FormGroupControls<SearchField> = this.dialogStrategy.form;

  public isUsersSelected = false;
  public suggestedUsers: UserSelectValue[] = [];
  public selectedUsers: UserSelectValue[] = [];

  public ngOnInit(): void {
    this.messageForm.controls.shareInput.valueChanges.subscribe((selectedUsers: UserSelectValue[]) => {
      this.selectedUsers = selectedUsers;
      this.isUsersSelected = selectedUsers.length > 0;
      this.onSelectedUsersChange();
    });
  }

  public ngOnDestroy(): void {
    this.sendMessageFormService.resetFormStatus();
  }

  public actionsHandler(action: DialogActionTypes): void {
    this.dialogActions[action]();
  }

  public submit(value: string): void {
    this.isUsersSelected = this.dialogStrategy.submitSuccessfully(value, this.selectedUsers);
    this.messageForm.markAllAsTouched();
  }

  public onSearchKeywordChanged(event: KeyboardEvent & { target: HTMLInputElement }): void {
    this.usersSuggestionService
      .suggestUsers(this, event.target.value)
      .then((users: ShareUserViewModel[]) => {
        this.suggestedUsers = shareUserViewModelToUserSelectValueAdapter(users);
      })
      .catch(() => {
        this.suggestedUsers = [];
      })
      .finally(() => this.detectChanges());
  }

  public onOpenDropdown(): void {
    setTimeout(() => this.searchInput.nativeElement.focus());
  }

  public onDeSelectAllUsers(): void {
    this.selectedUsers = [];
  }

  public onSelectedUsersChange(): void {
    this.sendMessageFormService.updateSelectedUsersValue(this.isUsersSelected);
  }

  private detectChanges(): void {
    safelyDetectChanges(this.detector);
  }
}
